// Web Search Tool - Concrete web search implementation
// Extends WorkspaceTool - provides web search functionality via Wikipedia API

import { WorkspaceTool } from "../../base/WorkspaceTool.js";
import { logger } from "../../../../../../core/logger.js";

export class WebSearchTool extends WorkspaceTool {
  constructor() {
    super();
    this.toolName = 'webSearchTool';
  }

  /**
   * Search the web using Wikipedia opensearch API
   * @param {string} query - Search query
   * @param {number} maxResults - Maximum results to return (default 5)
   * @returns {Promise<Object>} Search results
   */
  async webSearch(query, maxResults = 5) {
    try {
      // Validate inputs
      if (!query || typeof query !== 'string' || query.trim().length === 0) {
        return this.returnError({}, 'Valid search query is required');
      }

      if (maxResults < 1 || maxResults > 10) {
        maxResults = 5; // Default to 5 if invalid
      }

      // Construct Wikipedia opensearch API URL
      const searchParams = new URLSearchParams({
        action: 'opensearch',
        search: query.trim(),
        limit: maxResults.toString(),
        namespace: '0', // Main articles only
        format: 'json',
        origin: '*' // Enable CORS
      });

      const searchUrl = `https://en.wikipedia.org/w/api.php?${searchParams.toString()}`;

      // Make the request with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000);

      const response = await fetch(searchUrl, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'Revolt-WebSearch/1.0'
        }
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      let data = await response.json();


      // Check if we got results, if not try a simplified query
      if (!data[1] || data[1].length === 0) {
        const simplifiedQuery = this.simplifyQuery(query);

        if (simplifiedQuery !== query) {
          // Try the simplified query
          const simplifiedSearchParams = new URLSearchParams({
            action: 'opensearch',
            search: simplifiedQuery,
            limit: maxResults.toString(),
            namespace: '0', // Main articles only
            format: 'json',
            origin: '*' // Enable CORS
          });

          const simplifiedSearchUrl = `https://en.wikipedia.org/w/api.php?${simplifiedSearchParams.toString()}`;

          try {
            const simplifiedController = new AbortController();
            const simplifiedTimeoutId = setTimeout(() => simplifiedController.abort(), 8000);

            const simplifiedResponse = await fetch(simplifiedSearchUrl, {
              signal: simplifiedController.signal,
              headers: {
                'Accept': 'application/json',
                'User-Agent': 'Revolt-WebSearch/1.0'
              }
            });

            clearTimeout(simplifiedTimeoutId);

            if (simplifiedResponse.ok) {
              data = await simplifiedResponse.json();
            }
          } catch (error) {
            logger.warn(
              {
                tags: "tools|websearch|fallback",
                color1: "yellow",
              },
              `Simplified query failed for "${query}": ${error.message}`
            );
          }
        }
      }

      // Get extracts for top 2 results to provide content
      const formattedResults = this.formatResults(data, maxResults);

      const resultsWithContent = await this.enrichResultsWithContent(formattedResults.slice(0, 2)); // Only top 2 to avoid rate limits

      // Format and return results
      return this.returnSuccess({
        query,
        answer: null, // Wikipedia doesn't provide instant answers like DuckDuckGo
        abstract: null,
        results: resultsWithContent,
        source: 'Wikipedia',
        totalResults: data[1]?.length || 0
      }, 'Web search completed successfully');

    } catch (error) {
      if (error.name === 'AbortError') {
        return this.returnError({}, 'Search request timed out');
      }
      return this.returnError({}, `Search failed: ${error.message}`);
    }
  }

  /**
   * Format search results from Wikipedia opensearch response
   * Response format: [query, [titles], [descriptions], [urls]]
   */
  formatResults(data, maxResults) {
    if (!Array.isArray(data) || data.length < 4) {
      return [];
    }

    const [, titles, descriptions, urls] = data; // Skip query (index 0), destructure titles, descriptions, urls

    if (!Array.isArray(titles) || !Array.isArray(urls)) {
      return [];
    }

    return titles
      .slice(0, maxResults)
      .map((title, index) => ({
        title: title || 'Search Result',
        description: descriptions?.[index] || '',
        url: urls?.[index] || null
      }))
      .filter(result => result.title && result.url); // Filter out invalid results
  }

  /**
   * Enrich search results with page content extracts
   * Fetches Wikipedia extracts for the top results to provide content
   */
  async enrichResultsWithContent(results) {
    const enrichedResults = [];

    for (const result of results) {
      try {
        // Extract page title from URL
        const pageTitle = this.extractPageTitleFromUrl(result.url);
        if (!pageTitle) {
          enrichedResults.push(result);
          continue;
        }

        // Fetch page extract
        const extract = await this.fetchWikipediaExtract(pageTitle);

        // Add content to result (limit to ~300 words)
        enrichedResults.push({
          ...result,
          content: extract ? this.limitContent(extract, 300) : null
        });

      } catch (error) {
        logger.warn(
          {
            tags: "tools|websearch|content",
            color1: "yellow",
          },
          `Failed to fetch content for "${result.title}": ${error.message}`
        );
        // Still include the result even if content fetch fails
        enrichedResults.push(result);
      }
    }

    return enrichedResults;
  }

  /**
   * Extract page title from Wikipedia URL
   */
  extractPageTitleFromUrl(url) {
    if (!url) return null;
    try {
      const urlObj = new URL(url);
      if (urlObj.hostname.includes('wikipedia.org')) {
        // URL format: https://en.wikipedia.org/wiki/Page_Title
        const pathParts = urlObj.pathname.split('/');
        if (pathParts.length >= 3 && pathParts[1] === 'wiki') {
          return decodeURIComponent(pathParts.slice(2).join('/'));
        }
      }
    } catch (error) {
      logger.warn(
        {
          tags: "tools|websearch|url",
          color1: "yellow",
        },
        `Failed to parse Wikipedia URL "${url}": ${error.message}`
      );
    }
    return null;
  }

  /**
   * Fetch Wikipedia page extract using the API
   */
  async fetchWikipediaExtract(pageTitle) {
    const extractParams = new URLSearchParams({
      action: 'query',
      prop: 'extracts',
      titles: pageTitle,
      exintro: '1', // Only intro section
      explaintext: '1', // Plain text
      exchars: '1000', // Limit to 1000 characters
      format: 'json',
      origin: '*' // Enable CORS
    });

    const extractUrl = `https://en.wikipedia.org/w/api.php?${extractParams.toString()}`;

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000); // Shorter timeout for extracts

    try {
      const response = await fetch(extractUrl, {
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
          'User-Agent': 'Revolt-WebSearch/1.0'
        }
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        return null;
      }

      const data = await response.json();

      // Extract the page content from the API response
      const pages = data.query?.pages;
      if (pages) {
        const pageId = Object.keys(pages)[0];
        return pages[pageId]?.extract || null;
      }

      return null;

    } catch (error) {
      clearTimeout(timeoutId);
      logger.warn(
        {
          tags: "tools|websearch|extract",
          color1: "yellow",
        },
        `Failed to fetch Wikipedia extract for "${pageTitle}": ${error.message}`
      );
      return null;
    }
  }

  /**
   * Limit content to approximately word count
   */
  limitContent(text, maxWords) {
    if (!text) return null;

    const words = text.split(/\s+/);
    if (words.length <= maxWords) {
      return text;
    }

    // Take first maxWords and add ellipsis
    return words.slice(0, maxWords).join(' ') + '...';
  }

  /**
   * Simplify a complex query to its main subject
   * E.g., "Leonardo DiCaprio biography movies and career" → "Leonardo DiCaprio"
   */
  simplifyQuery(query) {
    if (!query) return query;

    // Common patterns to remove
    const patternsToRemove = [
      /\s+(biography|bio|life|story|background|history)$/i,
      /\s+(movies|films|filmography|cinematography)$/i,
      /\s+(career|professional|work|acting)$/i,
      /\s+(and|or|with|about|on|in|for|of|the|a|an)$/gi,
      /\s+(overview|summary|guide|tutorial|introduction|beginner)$/i,
      /\s+(features|characteristics|traits|qualities)$/i,
      /\s+(examples|samples|instances)$/i
    ];

    let simplified = query;

    // Remove common trailing words
    for (const pattern of patternsToRemove) {
      simplified = simplified.replace(pattern, '');
    }

    // If the query is still complex (multiple words), take just the first 1-2 words
    // that are likely to be the main subject
    const words = simplified.trim().split(/\s+/);

    // Look for proper nouns (capitalized words) as they're often the main subject
    const capitalizedWords = words.filter(word => word.length > 0 && word[0] === word[0].toUpperCase());

    if (capitalizedWords.length >= 2) {
      // Take first 2 capitalized words (likely first/last name)
      return capitalizedWords.slice(0, 2).join(' ');
    } else if (words.length > 2) {
      // Fallback: take first 2 words
      return words.slice(0, 2).join(' ');
    }

    // Return as-is if already simple
    return simplified.trim() || query;
  }
}